import React, { useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomTimePicker from "../../component/elements/customTimePicker";
import CustomButton from "../../component/elements/CustomButton/CustomButton";
import axiosInstance from "../../configuration/request";
import { useSelector } from "react-redux";
import { useSnackbar } from "../../component/SnackbarContext";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import StripUnauthorised from "../../component/stripeUnauthorised";

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const initialSchedule = daysOfWeek.reduce((acc, day) => {
  acc[day] = { from: null, to: null, off: false, note: "" };
  return acc;
}, {});

function AvailabilityCalendar() {
  const { loginDetails, isStripeAccount } = useSelector(
    (state) => state.counter
  );
  const userId = loginDetails?.userId || null;

  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState(initialSchedule);
  const [applyAllFrom, setApplyAllFrom] = useState(null);
  const [applyAllTo, setApplyAllTo] = useState(null);
  const [deliveryPersonData, setDeliveryPersonData] = useState(null);
  const [errorDays, setErrorDays] = useState({});

  useEffect(() => {
    if (userId) fetchUser();
  }, [userId]);

  /* -------------------------------- HELPERS -------------------------------- */

  const isFromBeforeTo = (from, to) => {
    if (!dayjs.isDayjs(from) || !dayjs.isDayjs(to)) return true;
    return from.isBefore(to);
  };

  /* ------------------------------- API CALL -------------------------------- */

  const fetchUser = () => {
    axiosInstance
      .get(`/customer/users?id=${userId}`)
      .then((res) => {
        const data = res?.data?.data?.[0];
        if (!data?.deliveryPerson) return;

        const workingHours = data.deliveryPerson.working_hours || {};
        setDeliveryPersonData(data.deliveryPerson);

        const transformed = daysOfWeek.reduce((acc, day) => {
          const open = workingHours[day]?.open;
          const close = workingHours[day]?.close;

          acc[day] = {
            from: open ? dayjs(open, "hh:mm A") : null,
            to: close ? dayjs(close, "hh:mm A") : null,
            note: workingHours[day]?.note || "",
            off: !open || !close,
          };
          return acc;
        }, {});

        setSchedule(transformed);
      })
      .catch(() => showSnackbar("Something went wrong!", "error"));
  };

  /* ------------------------------- HANDLERS -------------------------------- */

  const handleTimeChange = (day, field, value) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
        off: false,
      },
    }));

    setErrorDays((prev) => {
      const updated = {
        ...schedule[day],
        [field]: value,
      };
      const isInvalid =
        updated.from &&
        updated.to &&
        !isFromBeforeTo(updated.from, updated.to);

      if (isInvalid) {
        showSnackbar(`Start time must be before end time on ${day}`, "error");
      }
      return { ...prev, [day]: isInvalid };
    });
  };

  const handleDayOff = (day) => {
    setSchedule((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        off: !prev[day].off,
        from: !prev[day].off ? null : prev[day].from,
        to: !prev[day].off ? null : prev[day].to,
      },
    }));
  };

  const handleApplyToAll = () => {
    if (!isFromBeforeTo(applyAllFrom, applyAllTo)) {
      showSnackbar("Start time must be before end time", "error");
      return;
    }

    setSchedule((prev) => {
      const updated = { ...prev };
      daysOfWeek.forEach((day) => {
        if (!updated[day].off) {
          updated[day].from = applyAllFrom;
          updated[day].to = applyAllTo;
        }
      });
      return updated;
    });
  };

  const handleSave = () => {
    const invalid = Object.values(errorDays).some(Boolean);
    if (invalid) {
      showSnackbar("Please fix invalid times.", "error");
      return;
    }

    const incomplete = Object.values(schedule).some(
      (v) => !v.off && ((v.from && !v.to) || (!v.from && v.to))
    );
    if (incomplete) {
      showSnackbar("Please complete From & To times.", "error");
      return;
    }

    const formatted = {};
    Object.entries(schedule).forEach(([day, value]) => {
      formatted[day] = {
        open: value.from ? value.from.format("hh:mm A") : null,
        close: value.to ? value.to.format("hh:mm A") : null,
        note: "",
      };
    });

    axiosInstance
      .patch(`/customer/users/${userId}`, {
        deliveryPerson: {
          ...deliveryPersonData,
          working_hours: formatted,
        },
      })
      .then(() => {
        showSnackbar("Schedule updated successfully", "success");
        fetchUser();
      })
      .catch(() => showSnackbar("Something went wrong!", "error"));
  };

  /* ---------------------------- GOOGLE PLACEHOLDER --------------------------- */

  const syncCalendar = () => {
    if (!window.google) {
      showSnackbar("Google SDK not loaded yet", "warning");
      return;
    }
  };

  /* ---------------------------------- UI ----------------------------------- */

  if (!isStripeAccount) {
    return (
      <div className="pl-5 pr-6">
        <h2 className="text-2xl font-semibold mb-2">Weekly Availability</h2>
        <StripUnauthorised
          handleBackProfile={() => navigate("/delivery/profile")}
        />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white rounded-2xl shadow space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Weekly Availability</h2>
        <CustomButton
          onClick={syncCalendar}
          customeClass="light-blue-outlined-dark w-[200px] h-[35px]"
        >
          Link Google Calendar
        </CustomButton>
      </div>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="flex gap-4 items-center border p-4 rounded-xl bg-gray-50">
          <CustomTimePicker value={applyAllFrom} onChange={setApplyAllFrom} />
          <CustomTimePicker value={applyAllTo} onChange={setApplyAllTo} />
          <CustomButton onClick={handleApplyToAll}>
            Apply to All
          </CustomButton>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {daysOfWeek.map((day) => (
            <div key={day} className="border p-4 rounded-xl bg-gray-50">
              <div className="flex justify-between mb-3">
                <h3 className="font-semibold">{day}</h3>
                <button
                  onClick={() => handleDayOff(day)}
                  className="text-xs px-3 py-1 rounded bg-blue-100"
                >
                  {schedule[day].off ? "Marked Off" : "Mark Off"}
                </button>
              </div>

              {!schedule[day].off && (
                <div className="flex gap-3">
                  <CustomTimePicker
                    value={schedule[day].from}
                    onChange={(v) => handleTimeChange(day, "from", v)}
                  />
                  <CustomTimePicker
                    value={schedule[day].to}
                    onChange={(v) => handleTimeChange(day, "to", v)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </LocalizationProvider>

      <div className="flex justify-center">
        <CustomButton onClick={handleSave} customeClass="gradient-button">
          Save Changes
        </CustomButton>
      </div>
    </div>
  );
}

export default AvailabilityCalendar;
